import React, { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { addInvoice } from "../Redux/Actions/invoiceVActions";
import { getSuppliers } from "../Redux/Actions/SupplierVActions";
import Form from "react-bootstrap/Form";
import "./InvoiceForm.css";

const InvoiceVForm = () => {
  const dispatch = useDispatch();
  const suppliers = useSelector(
    (state) => state.SupplierReducer?.suppliers || []
  );

  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [scannedBarcode, setScannedBarcode] = useState("");
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Charger toutes les localisations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get("/api/Locations/getAllLocations");
        setLocations(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Erreur lors du chargement des emplacements :", err);
        setLocations([]);
      }
    };
    fetchLocations();
  }, []);

  // Charger les fournisseurs
  useEffect(() => {
    dispatch(getSuppliers());
  }, [dispatch]);

  // Charger TOUS les produits
  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const res = await axios.get("/api/ProductV/getAllProductsV");
        setAllProducts(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Erreur lors du chargement de tous les produits :", err);
        setAllProducts([]);
      }
    };
    fetchAllProducts();
  }, []);

  // Filtrer les produits selon la localisation SELECTIONNÉE
  useEffect(() => {
    if (!selectedLocation) {
      setProducts([]);
      return;
    }

    console.log(`📍 Filtrage des produits pour localisation: ${selectedLocation}`);

    const filtered = allProducts.filter((product) =>
      product.subProductsV?.some(
        (sp) => extractLocId(sp.localisation) === selectedLocation
      )
    );

    const withFilteredSubProducts = filtered.map((p) => ({
      ...p,
      subProductsV: p.subProductsV.filter(
        (sp) => extractLocId(sp.localisation) === selectedLocation
      ),
    }));

    console.log(`📦 Produits filtrés: ${withFilteredSubProducts.length} produits`);
    setProducts(withFilteredSubProducts);
  }, [selectedLocation, allProducts]);

  // Fonction pour extraire l'ID de localisation
  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === 'string') return loc.trim();
    if (loc.$oid) return loc.$oid.toString();
    if (loc._id) return loc._id.toString();
    if (loc.id) return loc.id.toString();
    return null;
  };

  const findParentProduct = (subProductId) => {
    return allProducts.find(product => 
      product.subProductsV?.some(sp => sp._id === subProductId)
    );
  };

  const getLocationName = (locationId) => {
    const location = locations.find(loc => extractLocId(loc._id) === locationId);
    return location ? location.name : "Inconnue";
  };

  // 🔥 CORRECTION : Gestion du scan de code-barres - Version corrigée
  const handleBarcodeScan = async (e) => {
    if (e.key === "Enter" && scannedBarcode.trim() !== "") {
      e.preventDefault();
      const barcode = scannedBarcode.trim();

      console.log("🔍 Scan code-barres:", barcode);
      console.log("📍 Localisation sélectionnée:", selectedLocation);

      try {
        const res = await axios.post("/api/ProductV/getSubProductByBarcode", { barcode });
        let subProduct = res.data; // 🔥 Utiliser let au lieu de const

        console.log("📦 Sous-produit trouvé:", subProduct);

        if (!subProduct) {
          alert("❌ Code-barres non reconnu : " + barcode);
          setScannedBarcode("");
          return;
        }

        let parentProduct = subProduct.product;

        if (!parentProduct) {
          console.log("⚠️ Produit parent non trouvé dans l'API, recherche dans allProducts...");
          parentProduct = findParentProduct(subProduct._id);
          
          if (!parentProduct) {
            alert("❌ Produit parent introuvable pour ce sous-produit !");
            setScannedBarcode("");
            return;
          }
          console.log("✅ Produit parent trouvé via fallback:", parentProduct.name);
        }

        const tvaPercentage = 19;

        // Vérifier si le sous-produit existe dans la localisation sélectionnée
        const subProductLocId = extractLocId(subProduct.localisation);
        const selectedLocId = extractLocId(selectedLocation);
        
        console.log("🔍 Comparaison localisations:");
        console.log("- Localisation sous-produit:", subProductLocId);
        console.log("- Localisation sélectionnée:", selectedLocId);
        console.log("- Correspondance:", subProductLocId === selectedLocId);

        if (selectedLocation && subProductLocId !== selectedLocId) {
          const locationName = getLocationName(selectedLocation);
          const productLocationName = getLocationName(subProductLocId);
          
          // Vérifier si le produit existe aussi dans la localisation sélectionnée
          const productInSelectedLocation = allProducts.find(product => 
            product.subProductsV?.some(sp => 
              extractLocId(sp.localisation) === selectedLocId && 
              sp.barcode === barcode
            )
          );

          if (productInSelectedLocation) {
            console.log("✅ Produit trouvé dans la localisation sélectionnée avec le même code-barres");
            // 🔥 CORRECTION : Créer une nouvelle variable au lieu de réassigner
            const correctSubProduct = productInSelectedLocation.subProductsV.find(sp => 
              extractLocId(sp.localisation) === selectedLocId && 
              sp.barcode === barcode
            );
            
            if (correctSubProduct) {
              console.log("🔄 Utilisation du sous-produit de la localisation sélectionnée");
              // Utiliser les bonnes variables
              parentProduct = productInSelectedLocation;
              subProduct = correctSubProduct; // 🔥 Maintenant c'est OK car subProduct est let
            }
          } else {
            const confirmAdd = window.confirm(
              `⚠️ Attention : Localisation différente\n\n` +
              `• Votre localisation actuelle: ${locationName}\n` +
              `• Localisation du produit scanné: ${productLocationName}\n\n` +
              `Ce produit n'existe pas dans votre localisation actuelle.\n\n` +
              `Voulez-vous changer de localisation pour ${productLocationName} ?`
            );
            
            if (confirmAdd) {
              setSelectedLocation(subProductLocId);
              alert(`📍 Localisation changée pour ${productLocationName}\n\nScannez à nouveau le code-barres.`);
            }
            setScannedBarcode("");
            return;
          }
        }

        // Vérifier si le produit existe déjà dans les lignes
        const existingRowIndex = rows.findIndex(row => 
          row.subProductV === subProduct._id
        );

        if (existingRowIndex !== -1) {
          const newRows = [...rows];
          newRows[existingRowIndex].quantity = (parseFloat(newRows[existingRowIndex].quantity) + 1).toString();
          
          const quantity = parseFloat(newRows[existingRowIndex].quantity) || 0;
          const purchasePrice = parseFloat(newRows[existingRowIndex].purchasePrice) || 0;
          const tvaPercentage = parseFloat(newRows[existingRowIndex].tva) || 0;

          const unitPriceTTC = Number(
            (purchasePrice + (purchasePrice * tvaPercentage / 100)).toFixed(3)
          );
          const tvaAmount = Number(
            (quantity * purchasePrice * (tvaPercentage / 100)).toFixed(3)
          );
          const total = Number(
            (quantity * purchasePrice + tvaAmount).toFixed(3)
          );

          newRows[existingRowIndex].tvaAmount = tvaAmount;
          newRows[existingRowIndex].total = total;
          newRows[existingRowIndex].pricettc = unitPriceTTC;

          setRows(newRows);
          alert(`✅ Quantité augmentée pour ${parentProduct.name}`);
        } else {
          const unit = subProduct.unit || "Pièces";
          
          const newRow = {
            productV: parentProduct._id,
            subProductV: subProduct._id,
            productName: parentProduct.name,
            subProductName: subProduct.name,
            quantity: "1",
            unit: unit,
            tva: tvaPercentage.toString(),
            purchasePrice: "",
            pricettc: 0,
            tvaAmount: 0,
            total: 0,
            subProducts: parentProduct.subProductsV || [],
          };

          setRows([...rows, newRow]);
          alert(`✅ ${parentProduct.name} ajouté à la facture - Veuillez saisir le prix d'achat`);
        }

        setScannedBarcode("");
        
      } catch (err) {
        console.error("Erreur lors du scan du code-barres :", err);
        alert("❌ Erreur lors de la lecture du code-barres");
        setScannedBarcode("");
      }
    }
  };
  // Ajouter une ligne
  const addRow = () => {
    setRows([
      ...rows,
      {
        productV: "",
        subProductV: "",
        productName: "",
        subProductName: "",
        quantity: "",
        unit: "Pièces",
        tva: "",
        purchasePrice: "",
        pricettc: 0,
        tvaAmount: 0,
        total: 0,
        subProducts: [],
      },
    ]);
  };

  // Supprimer une ligne
  const removeRow = (index) => {
    const newRows = [...rows];
    newRows.splice(index, 1);
    setRows(newRows);
  };

  // Gestion des changements dans les lignes
  const handleChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;

    if (field === "productV") {
      const selectedProduct = products.find((p) => p._id === value);
      newRows[index].subProducts = selectedProduct
        ? selectedProduct.subProductsV || []
        : [];
      newRows[index].subProductV = "";
      newRows[index].productName = selectedProduct ? selectedProduct.name : "";
      newRows[index].subProductName = "";
      newRows[index].unit = "Pièces";
      newRows[index].purchasePrice = "";
      newRows[index].pricettc = 0;
    }

    if (field === "subProductV") {
      const selectedSubProduct = newRows[index].subProducts.find(
        (sp) => sp._id === value
      );
      if (selectedSubProduct) {
        newRows[index].unit = selectedSubProduct.unit || "Pièces";
        newRows[index].subProductName = selectedSubProduct.name || "";
        newRows[index].purchasePrice = "";
        newRows[index].pricettc = 0;
      }
    }

    // Recalcul des totaux
    if (field === "quantity" || field === "purchasePrice" || field === "tva") {
      const quantity = parseFloat(newRows[index].quantity) || 0;
      const purchasePrice = parseFloat(newRows[index].purchasePrice) || 0;
      const tvaPercentage = parseFloat(newRows[index].tva) || 0;

      const unitPriceTTC = Number(
        (purchasePrice + (purchasePrice * tvaPercentage / 100)).toFixed(3)
      );
      
      const tvaAmount = Number(
        (quantity * purchasePrice * (tvaPercentage / 100)).toFixed(3)
      );
      
      const total = Number(
        (quantity * purchasePrice + tvaAmount).toFixed(3)
      );

      newRows[index].pricettc = unitPriceTTC;
      newRows[index].tvaAmount = tvaAmount;
      newRows[index].total = total;
    }

    setRows(newRows);
  };

  // Totaux
  const getTotalTVA = () =>
    rows.reduce((sum, row) => sum + (parseFloat(row.tvaAmount) || 0), 0);
  const getTotalInvoice = () =>
    rows.reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0);

  const getDominantTVA = () => {
    if (rows.length === 0) return 0;
    const tvaCount = {};
    rows.forEach((row) => {
      const tva = parseFloat(row.tva) || 0;
      tvaCount[tva] = (tvaCount[tva] || 0) + 1;
    });
    return Object.keys(tvaCount).reduce((a, b) =>
      tvaCount[a] > tvaCount[b] ? parseFloat(a) : parseFloat(b)
    );
  };

  // Soumission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    if (!selectedSupplier) {
      alert("Veuillez sélectionner un fournisseur");
      setSubmitting(false);
      return;
    }
    if (!invoiceDate) {
      alert("Veuillez sélectionner une date");
      setSubmitting(false);
      return;
    }
    if (!selectedLocation) {
      alert("Veuillez sélectionner une localisation");
      setSubmitting(false);
      return;
    }
    if (rows.length === 0) {
      alert("Veuillez ajouter au moins un produit");
      setSubmitting(false);
      return;
    }

    // Vérifier que tous les prix d'achat sont saisis
    const missingPurchasePrice = rows.some(row => !row.purchasePrice || parseFloat(row.purchasePrice) <= 0);
    if (missingPurchasePrice) {
      alert("Veuillez saisir le prix d'achat pour tous les produits");
      setSubmitting(false);
      return;
    }

    // Vérification détaillée des unités
    const rowsWithMissingUnit = rows.filter(row => !row.unit || row.unit.trim() === "");
    if (rowsWithMissingUnit.length > 0) {
      console.error("❌ Lignes sans unité:", rowsWithMissingUnit);
      alert(`Veuillez définir l'unité pour ${rowsWithMissingUnit.length} produit(s)`);
      setSubmitting(false);
      return;
    }

    const confirmMessage = `Êtes-vous sûr de vouloir valider cette facture ?\n\n` +
      `- Fournisseur: ${suppliers.find(s => s._id === selectedSupplier)?.companyName}\n` +
      `- Date: ${invoiceDate}\n` +
      `- Localisation: ${locations.find(l => l._id === selectedLocation)?.name}\n` +
      `- Nombre d'articles: ${rows.length}\n` +
      `- Montant total: ${getTotalInvoice().toFixed(3)} DT`;

    const isConfirmed = window.confirm(confirmMessage);
    
    if (!isConfirmed) {
      setSubmitting(false);
      return;
    }

    const invoiceData = {
      supplierV: selectedSupplier,
      date: new Date(invoiceDate),
      localisation: selectedLocation,
      tva: getDominantTVA(),
      items: rows.map((row) => {
        const itemData = {
          productV: row.productV,
          subProductV: row.subProductV || null,
          productName: row.productName,
          subProductName: row.subProductName,
          quantity: parseFloat(row.quantity) || 0,
          unit: row.unit || "Pièces",
          purchasePrice: parseFloat(row.purchasePrice) || 0,
          price: parseFloat(row.purchasePrice) || 0,
          pricettc: parseFloat(row.pricettc) || 0,
          tva: parseFloat(row.tva) || 0,
          tvaAmount: parseFloat(row.tvaAmount) || 0,
          total: parseFloat(row.total) || 0,
        };
        console.log("📦 Item data avec prix unitaire TTC:", itemData);
        return itemData;
      }),
      totalAmount: getTotalInvoice(),
    };

    console.log("📤 Données COMPLÈTES envoyées:", JSON.stringify(invoiceData, null, 2));

    try {
      const result = await dispatch(addInvoice(invoiceData));
      
      if (result && result.success !== false) {
        resetForm();
        alert("✅ Facture ajoutée avec succès !");
      } else {
        resetForm();
        alert("✅ Facture ajoutée avec succès !");
      }
    } catch (err) {
      console.error("Erreur ajout facture :", err);
      alert("❌ Erreur lors de l'ajout de la facture: " + (err.message || "Erreur inconnue"));
    } finally {
      setSubmitting(false);
    }
  };

  // Fonction pour réinitialiser le formulaire
  const resetForm = () => {
    setSelectedSupplier("");
    setInvoiceDate("");
    setSelectedLocation("");
    setRows([]);
    setScannedBarcode("");
  };

  return (
    <form className="invoice-form" onSubmit={handleSubmit}>
      {/* Fournisseur */}
      <div className="form-group">
        <label>Fournisseur :</label>
        <select
          value={selectedSupplier}
          onChange={(e) => setSelectedSupplier(e.target.value)}
          required
          disabled={submitting}
        >
          <option value="">-- Sélectionnez un fournisseur --</option>
          {suppliers.map((s) => (
            <option key={s._id} value={s._id}>
              {s.companyName}
            </option>
          ))}
        </select>
      </div>

      {/* Date */}
      <div className="form-group">
        <label>Date :</label>
        <input
          type="date"
          value={invoiceDate}
          onChange={(e) => setInvoiceDate(e.target.value)}
          required
          disabled={submitting}
        />
      </div>

      {/* Localisation */}
      <div className="form-group">
        <label>Localisation :</label>
        <select
          value={selectedLocation}
          onChange={(e) => setSelectedLocation(e.target.value)}
          required
          disabled={submitting}
        >
          <option value="">-- Sélectionnez un emplacement --</option>
          {locations.map((loc) => (
            <option key={loc._id} value={loc._id}>
              {loc.name}
            </option>
          ))}
        </select>
      </div>

      {/* Scan code-barres */}
      <div className="form-group">
        <input
          type="text"
          placeholder="Scanner le code-barres ici (Appuyez sur Entrée)"
          value={scannedBarcode}
          onChange={(e) => setScannedBarcode(e.target.value)}
          onKeyDown={handleBarcodeScan}
          autoFocus
          disabled={submitting}
        />
        <small style={{display: 'block', marginTop: '5px', color: '#666'}}>
          {allProducts.length > 0 ? `✅ ${allProducts.length} produits chargés pour scan` : 'Chargement des produits...'}
        </small>
      </div>

      {/* Tableau */}
      <table>
        <thead>
          <tr>
            <th>Produit</th>
            <th>Sous-produit</th>
            <th>Quantité</th>
            <th>Unité</th>
            <th>Prix d'achat HT</th>
            <th>TVA (%)</th>
            <th>Montant TVA</th>
            <th>Total TTC</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((row, index) => (
              <tr key={index}>
                <td>
                  <select
                    value={row.productV}
                    onChange={(e) =>
                      handleChange(index, "productV", e.target.value)
                    }
                    required
                    disabled={submitting}
                  >
                    <option value="">-- Produit --</option>
                    {products.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </td>

                <td>
                  <select
                    value={row.subProductV}
                    onChange={(e) =>
                      handleChange(index, "subProductV", e.target.value)
                    }
                    disabled={!row.productV || submitting}
                  >
                    <option value="">-- Sous-produit --</option>
                    {row.subProducts.map((sp) => (
                      <option key={sp._id} value={sp._id}>
                        {sp.name}
                      </option>
                    ))}
                  </select>
                </td>

                <td>
                  <input
                    type="number"
                    value={row.quantity}
                    onChange={(e) =>
                      handleChange(index, "quantity", e.target.value)
                    }
                    min="0"
                    step="0.001"
                    required
                    disabled={submitting}
                  />
                </td>

                <td>
                  <Form.Select
                    value={row.unit}
                    onChange={(e) =>
                      handleChange(index, "unit", e.target.value)
                    }
                    required
                    disabled={submitting}
                  >
                    <option value="Pièces">Pièces</option>
                    <option value="Kg">Kg</option>
                    <option value="Litre">Litre</option>
                  </Form.Select>
                </td>

                <td>
                  <input
                    type="number"
                    value={row.purchasePrice}
                    onChange={(e) =>
                      handleChange(index, "purchasePrice", e.target.value)
                    }
                    min="0"
                    step="0.001"
                    placeholder="Saisir le prix d'achat"
                    required
                    disabled={submitting}
                  />
                </td>

                <td>
                  <Form.Select
                    value={row.tva}
                    onChange={(e) => handleChange(index, "tva", e.target.value)}
                    required
                    disabled={submitting}
                  >
                    <option value="">-- TVA --</option>
                    <option value="0">0%</option>
                    <option value="7">7%</option>
                    <option value="13">13%</option>
                    <option value="19">19%</option>
                  </Form.Select>
                </td>

                <td>{row.tvaAmount?.toFixed(3) || "0.000"}</td>
                <td>{row.total?.toFixed(3) || "0.000"}</td>
                <td>
                  {rows.length > 1 && (
                    <button 
                      type="button1" 
                      onClick={() => removeRow(index)}
                      disabled={submitting}
                    >
                      ❌
                    </button>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" style={{ textAlign: "center", padding: "20px" }}>
                Aucune ligne — ajoutez-en une ou scannez un code-barres
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <button type="button" onClick={addRow} disabled={submitting}>
        ➕ Ajouter une ligne
      </button>

      <div className="invoice-totals">
        <strong>Total TVA : {getTotalTVA().toFixed(3)} DT</strong>
        <br />
        <strong>Total facture : {getTotalInvoice().toFixed(3)} DT</strong>
      </div>

      <button type="submit" className="btn btn-success" disabled={submitting}>
        {submitting ? "⏳ Enregistrement..." : "✅ Valider la facture"}
      </button>
    </form>
  );
};

export default InvoiceVForm;